import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, MessageCircle } from "lucide-react";
import { api } from "../lib/api.js";

/**
 * SomContextBanner — compact, non-collapsible banner that shows the parent SOM
 * context on every child-module detail surface (PEC, PCP, SWC).
 *
 * Visual style: navy left-border accent, two-line layout that gracefully shrinks
 * to one line on wide screens. Feels like a breadcrumb with context, not a card.
 *
 * Props:
 *  - case_: the full SOM document (hydrated). Either pass this OR caseId.
 *  - caseId: alternatively, the SOM id — the banner will fetch the SOM itself.
 *  - testid: optional override for the root data-testid (defaults to "som-context-banner")
 */
export default function SomContextBanner({ case_, caseId, testid = "som-context-banner" }) {
  const [data, setData] = useState(case_ || null);

  useEffect(() => {
    if (case_) { setData(case_); return; }
    if (!caseId) return;
    api.get(`/service-cases/${caseId}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [case_, caseId]);

  if (!data) return null;

  const feedback = data.last_feedback_status; // "Green" | "Yellow" | "Red" | null
  const daysSince = data.days_since_last_contact;
  const feedbackDot = feedback === "Red" ? "bg-[#DC3545]" :
                      feedback === "Yellow" ? "bg-[#FFC107]" :
                      feedback === "Green" ? "bg-[#28A745]" :
                      "bg-[#6C757D]";
  const feedbackLabel = daysSince != null
    ? `${daysSince} dia${daysSince === 1 ? "" : "s"} sem contacto`
    : "sem feedback registado";

  return (
    <div
      className="bg-[#F4F6F8] border-l-4 border-[#1F3864] rounded-r-sm px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
      data-testid={testid}
    >
      <span className="text-[9px] uppercase tracking-widest text-[#6C757D] font-semibold">
        Obra de Manutenção
      </span>

      <Link
        to={`/service-cases/${data.id}`}
        className="inline-flex items-center gap-1 font-mono font-semibold text-[#1F3864] hover:underline"
        data-testid={`${testid}-som-link`}
      >
        {data.number}
        <ExternalLink className="w-3 h-3" />
      </Link>

      <Separator />
      <span className="text-[#1A1A2E]" data-testid={`${testid}-customer`}>{data.customer_name || "—"}</span>

      <Separator />
      <span className="font-mono text-[#1A1A2E]" data-testid={`${testid}-vehicle`}>{data.vehicle_registration || "—"}</span>

      <Separator />
      <span className="text-[#1A1A2E]" data-testid={`${testid}-brand`}>
        {data.brand || "—"}{data.model ? ` ${data.model}` : ""}
      </span>

      <Separator />
      <span
        className="inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#1F3864]/10 text-[#1F3864] border border-[#1F3864]/20"
        data-testid={`${testid}-status`}
      >
        {data.current_status || "—"}
      </span>

      {data.service_advisor?.name && (
        <>
          <Separator />
          <span className="text-[#6C757D]">
            Advisor: <span className="text-[#1A1A2E]">{data.service_advisor.name}</span>
          </span>
        </>
      )}

      <Separator />
      <span className="inline-flex items-center gap-1 text-[#6C757D]" title={`Último estado de feedback: ${feedback || "—"}`} data-testid={`${testid}-feedback`}>
        <MessageCircle className="w-3 h-3" />
        <span className={`w-2 h-2 rounded-full ${feedbackDot}`} />
        {feedbackLabel}
      </span>
    </div>
  );
}

function Separator() {
  return <span className="text-[#CBD5E1] select-none">·</span>;
}
